import { Creative, CreativeProps, AdContext } from '../../types';
import { CreativeEventEmitter } from '../../event-emitter/creative-event-emitter';
import { ImageCreative } from '../image-creative';
import { CreativeTagEvent } from '../../enums';
import { CreativeTrackingService } from '../../services/creative-tracking/creative-tracking-service';
import { PlayerTrackingHandler } from '../../handlers/creative-tracking/player-tracking-handler';
import { PayloadBuilderDisplayStrategy } from '../../payload-builder-strategies/payload-builder-strategy-display';
import { ParametersExtractor } from '../../parameters-extractor/parameters-extractor';
import {
  AssetLoadEventNotifier,
  DisplayEventNotifier,
  LoadedEventNotifier,
  ShowEventNotifier,
  TimespentEventNotifier,
  ViewEventNotifier
} from '../../notifiers';
import { Player } from '../../types/player';
import { ViewabilityObserverFactory } from '../../services/viewability-observer/viewability-observer-factory';
import { AdContainer } from '../../ad-container/ad-container';
import { TrackingDisablingHelper } from '../../helpers/tracking-disabling-helper';

export interface PlayerOptions {
  window: Window;
  container: HTMLElement;
  adContext: AdContext;
  creativeProps: CreativeProps;
}

export class ExperiencePlayer implements Player {
  creativeEventEmitter = new CreativeEventEmitter();
  private creative: Creative | null = null;
  private htmlTag: HTMLElement | null = null;
  protected trackingHandler: PlayerTrackingHandler | null = null;
  private readonly eventNotifiers: Array<
    | ViewEventNotifier
    | ShowEventNotifier
    | TimespentEventNotifier
    | DisplayEventNotifier
    | LoadedEventNotifier
  > = [];

  constructor(
    options: { activateTrackHandler: boolean } = { activateTrackHandler: true },
    private readonly viewabilityObserverFactory?: ViewabilityObserverFactory
  ) {
    const parametersExtractor = new ParametersExtractor();

    if (
      options.activateTrackHandler &&
      TrackingDisablingHelper.isTrackingDisabled(parametersExtractor) === false
    ) {
      const creativeTrackingService = new CreativeTrackingService(
        new PayloadBuilderDisplayStrategy(parametersExtractor)
      );
      this.trackingHandler = new PlayerTrackingHandler(
        this,
        creativeTrackingService,
        parametersExtractor
      );
    }

    if (!viewabilityObserverFactory) {
      this.viewabilityObserverFactory = new ViewabilityObserverFactory(
        window,
        new AdContainer(window),
        new ParametersExtractor()
      );
    }
  }

  async startExperience(props: PlayerOptions): Promise<void> {
    this.creative = new ImageCreative(props.window.document);

    this.attachEventHandlers();

    this.htmlTag = this.creative!.render(props.creativeProps);

    const assetLoadPromise = this.setupAssetLoadSync(
      this.creative,
      this.htmlTag,
      props.container
    );

    this.htmlTag && props.container.appendChild(this.htmlTag as HTMLElement);

    await assetLoadPromise;
    this.initObserverAndEventHandlers(props.container);

    if (props.creativeProps.containerPolicy) {
      this.onResizeHandler(props.creativeProps);
      props.window.addEventListener('resize', () => {
        this.onResizeHandler(props.creativeProps);
      });
    }
  }

  addEventListener(
    event: CreativeTagEvent,
    callback: (evt?: Event) => void
  ): void {
    this.creativeEventEmitter.addEventListener(event, evt => callback(evt));
  }

  emit(event: CreativeTagEvent, evt?: Event): void {
    this.creativeEventEmitter.emit(event, evt);
  }

  attachEventHandlers(): void {
    [
      CreativeTagEvent.CreativeLoadingFailed,
      CreativeTagEvent.CreativeAssetLoad,
      CreativeTagEvent.CreativeLoaded,
      CreativeTagEvent.CreativeDisplay,
      CreativeTagEvent.CreativeView,
      CreativeTagEvent.CreativeTimespent,
      CreativeTagEvent.CreativeShow,
      CreativeTagEvent.CreativeClicked
    ].forEach(event => {
      this.creative!.addEventListener(event, this.emit.bind(this, event));
    });
  }

  initObserverAndEventHandlers(imageTagContainer: HTMLElement) {
    const observerService = this.viewabilityObserverFactory!.getViewabilityObserver();
    this.eventNotifiers.push(
      new LoadedEventNotifier(observerService, this.creative!),
      new ViewEventNotifier(observerService, this.creative!),
      new ShowEventNotifier(observerService, this.creative!),
      new TimespentEventNotifier(observerService, this.creative!),
      new DisplayEventNotifier(observerService, this.creative!)
    );

    observerService.observe(imageTagContainer);
  }

  async setupAssetLoadSync(
    creative: Creative,
    htmlTag: HTMLElement,
    container: HTMLElement
  ) {
    const assetLoadEventNotifier = new AssetLoadEventNotifier(
      creative,
      htmlTag,
      container
    );
    await assetLoadEventNotifier.waitForReady();
  }

  protected onResizeHandler(props: CreativeProps): void {
    if (!this.htmlTag) return;
    let newProps = { ...props };
    this.creative?.updateStyle(newProps);
  }
}
